import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCabinetFiling } from "@fortawesome/pro-regular-svg-icons";
import {
  faBook,
  faFileCode,
  faFileMagnifyingGlass,
  faFolderOpen,
  faGear,
  faKey,
  faLinkSimple,
  faMessageQuestion,
  faTableTree,
  faTelescope,
  faUserSecret,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartArea, faCode } from "@fortawesome/sharp-solid-svg-icons";

import {
  IssuesFilter as IssuesFilterProto,
  IssueTab as IssueTabProto,
  IssueType as IssueTypeProto,
} from "@protos/issues/v1/issues";
import { DEFAULT_BRANCHES_GROUP } from "@shared/constants";
import { timeLookbackRangeToSinceDate } from "@shared/utils";

import { useBasePath } from "../useBasePath";
import { useFindingCounts } from "../useFindingCounts";
import { useHasNewSecretsUI } from "../useHasNewSecretsUI";
import { useOrg } from "../useOrg";
import { useRoutePermissionMap } from "../useRoutePermissionMap";
import { useSecretsCount } from "../useSecretsCount";
import { useSupportsFeature } from "../useSupportsFeature";
import { useTimeLookbackRange } from "../useTimeLookbackRange";

interface NavigationLinkProps {
  to?: string;
  search?: string;
  label: string;
  betaLabel?: boolean;
  active?: boolean;
  icon?: React.ReactNode;
  count?: number;
  routes?: NavigationLinkProps[];
  isChild?: boolean;
  menuOpen?: boolean;
  secondaryLink?: boolean;
  rightSection?: React.ReactNode;
  isExternalLink?: boolean;
  isDropdownMenuItem?: boolean;
  visible?: boolean;
  isHoverCardItem?: boolean;
  hideIcon?: boolean;
  children?: React.ReactNode;
}

const Icon = ({ icon }: { icon: IconProp }) => {
  return <FontAwesomeIcon icon={icon} fixedWidth />;
};

// NOTE: hasPrimaryBranchCapabilities will temporarily passed in as we roll out the primary branch work
// We need to pass it in explicitly because we can't call another hook inside this function
export const generateDefaultScaFindingsSearchParams = (
  hasPrimaryBranchCapabilities: boolean,
  repo?: string
) => {
  const scaSearchParams: {
    [key: string]: string;
  } = hasPrimaryBranchCapabilities
    ? {
        primary: "true",
        tab: "open",
      }
    : {
        ref: DEFAULT_BRANCHES_GROUP,
        tab: "open",
      };
  if (repo) {
    scaSearchParams["repo"] = repo;
  }

  return new URLSearchParams(scaSearchParams).toString();
};

export const useNavigationLinks = () => {
  const [org] = useOrg();
  const [tlr] = useTimeLookbackRange();
  const permissionsMap = useRoutePermissionMap();
  const { showNewSecretsUI } = useHasNewSecretsUI();

  const hasPrimaryBranchCapabilities = useSupportsFeature(
    "hasPrimaryBranchCapabilities"
  );

  const branchQueryParam = hasPrimaryBranchCapabilities
    ? { onPrimaryBranch: true }
    : { refs: [DEFAULT_BRANCHES_GROUP] };

  // show issue counts for all open SAST issues on the default branch group
  const { data: sastFindingCounts } = useFindingCounts({
    filter: IssuesFilterProto.create({
      since: timeLookbackRangeToSinceDate(tlr),
      tab: IssueTabProto.OPEN,
      ...branchQueryParam,
    }),
    issueType: IssueTypeProto.SAST,
  });
  const { data: scaFindingCounts } = useFindingCounts({
    filter: IssuesFilterProto.create({
      since: timeLookbackRangeToSinceDate(tlr),
      tab: IssueTabProto.OPEN,
      ...branchQueryParam,
    }),
    issueType: IssueTypeProto.SCA,
  });
  const { data: secretsFindingCounts } = useFindingCounts({
    filter: IssuesFilterProto.create({
      since: timeLookbackRangeToSinceDate(tlr),
      tab: IssueTabProto.OPEN,
    }),
    issueType: IssueTypeProto.SECRETS,
  });

  const { data: oldSecretsCount } = useSecretsCount(true);

  const secretsCount = showNewSecretsUI
    ? secretsFindingCounts?.open
    : oldSecretsCount?.count;

  const location = useLocation();
  const basePath = useBasePath();

  const links: NavigationLinkProps[] = useMemo(
    () => [
      {
        label: "Dashboard",
        icon: <Icon icon={faChartArea} />,
        to: basePath,
        active:
          location.pathname === "/orgs/-" ||
          (org && location.pathname === `/orgs/${org.name}`),
        visible: permissionsMap.overview,
      },
      {
        label: "Projects",
        icon: <Icon icon={faFolderOpen} />,
        to: `${basePath}/projects/scanning`,
        active: location.pathname.includes("/projects"),
        visible: permissionsMap.projects,
      },
      {
        label: "Code",
        icon: <Icon icon={faCode} />,
        to: `${basePath}/findings`,
        search: `?tab=open&${
          hasPrimaryBranchCapabilities ? "primary=true" : "ref=_default"
        }`,
        count: sastFindingCounts?.open,
        active:
          location.pathname.includes("/findings") &&
          !location.pathname.includes("/supply-chain"), // distinct from the findings url to ensure that the Code item is highlighted when viewing a Code finding, and the Supply Chain item is highlighted when viewing a supply chain finding
        visible: permissionsMap.findings,
      },
      {
        label: "Secrets",
        icon: <Icon icon={faKey} />,
        active:
          location.pathname.includes("/secrets") &&
          !location.pathname.includes("policies"),
        to: `${basePath}/secrets`,
        search: showNewSecretsUI
          ? "?tab=open&validation_state=confirmed_valid,validation_error,no_validator"
          : "?status=open&validation_state=confirmed_valid,validation_error,no_validator",
        count: secretsCount,
        visible: permissionsMap.secrets,
      },
      {
        label: "Supply Chain",
        icon: <Icon icon={faLinkSimple} />,
        visible: permissionsMap.supplyChain,
        active: location.pathname.includes("/supply-chain"),
        to: `${basePath}/supply-chain/vulnerabilities`,
        search: `?${generateDefaultScaFindingsSearchParams(
          hasPrimaryBranchCapabilities
        ).toString()}`,
        count: scaFindingCounts?.open,
      },
      {
        label: "Rules",
        icon: <Icon icon={faFileMagnifyingGlass} />,
        active:
          location.pathname.includes("/board") ||
          location.pathname.includes("/policies") ||
          location.pathname.includes("/editor"),
        visible:
          permissionsMap.ruleBoard &&
          permissionsMap.editor &&
          permissionsMap.registry,
        routes: [
          {
            label: "Policies",
            to: `${basePath}/policies`,
            visible: permissionsMap.ruleBoard,
            active: location.pathname.includes("/policies"),
            icon: <Icon icon={faTableTree} />,
          },
          {
            label: "Editor",
            to: `${basePath}/editor`,
            active: location.pathname.includes("/editor"),
            visible: permissionsMap.editor,
            icon: <Icon icon={faFileCode} />,
          },
          {
            label: "Code Search",
            icon: <Icon icon={faTelescope} />,
            visible: permissionsMap.codeSearch,
            active: location.pathname.includes("/code-search"),
            to: `${basePath}/code-search`,
            betaLabel: true,
          },
          {
            label: "Registry",
            to: "/explore",
            isExternalLink: true,
            visible: permissionsMap.registry,
            icon: <Icon icon={faCabinetFiling} />,
          },
        ],
      },
      {
        label: "Settings",
        to: `${basePath}/settings`,
        active: location.pathname.includes("/settings"),
        secondaryLink: true,
        icon: <Icon icon={faGear} />,
        visible: permissionsMap.settings,
      },
      {
        label: "Docs",
        to: "https://semgrep.dev/docs/",
        secondaryLink: true,
        icon: <Icon icon={faBook} />,
        isExternalLink: true,
        visible: permissionsMap.docs,
      },
      {
        label: "Help",
        active: location.pathname.includes("/support"),
        to: `${basePath}/support`,
        secondaryLink: true,
        icon: <Icon icon={faMessageQuestion} />,
        visible: permissionsMap.support,
      },
      {
        label: "Admin",
        active: location.pathname.includes("/admin"),
        to: `/admin`,
        secondaryLink: true,
        visible: permissionsMap.admin,
        icon: <Icon icon={faUserSecret} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      basePath,
      location.pathname,
      org,
      sastFindingCounts?.open,
      scaFindingCounts?.open,
      secretsCount,
      permissionsMap,
    ]
  );

  return { links };
};
